<template>
  <div ref="component">
    <NovaDropdown
      :options="suggestions.map((p) => ({ label: p.text.text, value: p.placeId, item: p }))"
      :show="isDropdownOpen"
      :loading="isDropdownLoading"
      :max-height="200"
      @select:option="handleSelect"
    >
      <template #toggle>
        <NovaInputText
          v-if="!multiline"
          :id="fieldId"
          :model-value="searchText"
          :placeholder="placeholder"
          :disabled="disabled"
          left-icon="search"
          :readonly="readonly"
          :readonly-placeholder="$t('common.no-content')"
          @clear="emit('clear')"
          @update:model-value="handleInputUpdate"
        />
        <NovaTextarea
          v-else
          :id="fieldId"
          class="field-place_text-area"
          :model-value="searchText"
          :placeholder="placeholder"
          :autoresize="true"
          :disabled="disabled"
          :readonly="readonly"
          :error="showErrorIfNoSelection ? error : ''"
          :readonly-placeholder="$t('common.no-content')"
          left-icon="search"
          @clear="emit('clear')"
          @update:model-value="handleInputUpdate"
        />
      </template>
    </NovaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import { useDetectClickOutside } from "@/composables/useDetectClickOutside";
import { Option } from "@/types/Option";
import NovaInputText from "@/ui-kit/NovaInputText/NovaInputText.vue";
import NovaTextarea from "@/ui-kit/NovaTextarea/NovaTextarea.vue";
import NovaDropdown from "@/ui-kit/NovaDropdown/NovaDropdown.vue";
import { useGoogleMapsApi } from "@/features/google-maps/use-google-maps-api/useGoogleMapsApi";
import { useVModel, whenever } from "@vueuse/core";
import { usePlacesAutocomplete } from "@/features/google-maps/use-places-autocomplete";

export interface SelectedPlace {
  address: string;
  placeId: string;
  lat: number;
  lng: number;
  postalCode: string;
  item: google.maps.places.Place;
}

export interface FieldPlaceSearchProps {
  fieldId: string;
  modelValue: string;
  placeholder?: string;
  readonly?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  showErrorIfNoSelection?: boolean;
}

interface Events {
  (event: "update:modelValue", value: string): void;
  (event: "select", selection: SelectedPlace): void;
  (event: "clear"): void;
}

const props = withDefaults(defineProps<FieldPlaceSearchProps>(), {
  multiline: false,
});
const emit = defineEmits<Events>();
const component = ref<Element | null>(null);

const searchText = useVModel(props, "modelValue", emit, { passive: true });
const { isGoogleMapsLoaded, placesLibrary } = useGoogleMapsApi();
const { suggestions, loading, initPlaces } = usePlacesAutocomplete(searchText, {
  debounce: 500,
  minLengthAutocomplete: 3,
  placesLibrary,
});

const { $t } = useNuxtApp();
const isDropdownOpen = ref(false);
const isDropdownLoading = ref(false);
const error = computed(() => {
  if (props.modelValue !== searchText.value) return $t("field-place-search.error");
});

function handleInputUpdate(value: string) {
  searchText.value = value;

  if (value) {
    isDropdownLoading.value = true;
    isDropdownOpen.value = true;
  } else {
    isDropdownOpen.value = false;
  }
}

async function handleSelect(option: Option) {
  const prediction = option.item as google.maps.places.PlacePrediction;
  searchText.value = prediction.text.text;

  const place = prediction.toPlace();
  await place.fetchFields({ fields: ["location", "addressComponents"] });

  const addressComponents = place.addressComponents ?? [];
  const postalCode = addressComponents.find((c) => c.types.includes("postal_code"))?.longText ?? "";
  const location = place.location!;

  emit("select", {
    placeId: prediction.placeId,
    address: prediction.text.text,
    lat: location.lat(),
    lng: location.lng(),
    postalCode,
    item: place,
  });

  isDropdownOpen.value = false;
}

watch(loading, () => {
  if (!loading.value) {
    isDropdownLoading.value = false;
  }
});

whenever(isGoogleMapsLoaded, initPlaces);

useDetectClickOutside(component, () => {
  isDropdownOpen.value = false;
});
</script>

<style>
.field-place_text-area .InputText {
  min-height: 28px;
  padding: 4px;
}
</style>
